/**
 * Draft Suppliers table (Supplier Onboarding → Drafts tab).
 *
 * Extracted from OnboardingDashboard, which is well over the 500-line limit.
 *
 * CR-6654: adds a Delete row action for staff/superusers. Removing a mistaken
 * draft was previously a database-only fix.
 */
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Building2, CheckCircle2, Eye, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { suppliersAPI, apiErrorMessage } from '../../lib/api';
import type { SupplierProfile } from '../../lib/api';

interface DraftSuppliersTableProps {
  /** Drafts after the dashboard's search filter, before pagination. */
  filteredDraftSuppliers: SupplierProfile[];
  pagedDraftSuppliers: SupplierProfile[];
  searchQuery: string;
  draftPage: number;
  setDraftPage: (updater: (page: number) => number) => void;
  pageCount: (total: number) => number;
  pageSize: number;
  onContinue: (companyId: number) => void;
  /** Re-fetch the dashboard after a delete. */
  onChanged: () => void;
  /** Delete is staff-or-superuser only. */
  canDelete: boolean;
}

export default function DraftSuppliersTable({
  filteredDraftSuppliers,
  pagedDraftSuppliers,
  searchQuery,
  draftPage,
  setDraftPage,
  pageCount,
  pageSize,
  onContinue,
  onChanged,
  canDelete,
}: DraftSuppliersTableProps) {
  const [deleteTarget, setDeleteTarget] = useState<SupplierProfile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const companyIdOf = (sp: SupplierProfile): number =>
    (sp.company_id ?? sp.company) as number;
  const nameOf = (sp: SupplierProfile): string =>
    sp.company_name || `Supplier #${sp.id}`;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await suppliersAPI.deleteSupplier(companyIdOf(deleteTarget));
      toast.success(`"${nameOf(deleteTarget)}" deleted`);
      setDeleteTarget(null);
      onChanged();
    } catch (err: unknown) {
      // The backend refuses (400) when the supplier has transactional data —
      // surface that reason rather than a generic failure.
      toast.error(apiErrorMessage(err, 'Failed to delete supplier'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Draft Suppliers</CardTitle>
        <CardDescription>
          Wizard in progress. Submit the wizard to move the supplier to Pending Approvals.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {filteredDraftSuppliers.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'No drafts match your search' : 'No draft suppliers'}
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Started By</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className={canDelete ? 'w-[220px]' : 'w-[120px]'}>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedDraftSuppliers.map((sp) => (
                  <TableRow key={sp.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{nameOf(sp)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {sp.started_by || <span className="italic">Imported</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {sp.created_at
                        ? formatDistanceToNow(new Date(sp.created_at), { addSuffix: true })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => onContinue(companyIdOf(sp))}>
                          <Eye className="h-4 w-4 mr-1" />Continue
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(sp)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pageCount(filteredDraftSuppliers.length) > 1 && (
              <div className="flex items-center justify-between pt-4 text-sm">
                <span className="text-muted-foreground">
                  Showing {((draftPage - 1) * pageSize) + 1}–
                  {Math.min(draftPage * pageSize, filteredDraftSuppliers.length)} of{' '}
                  {filteredDraftSuppliers.length}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={draftPage === 1}
                    onClick={() => setDraftPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={draftPage >= pageCount(filteredDraftSuppliers.length)}
                    onClick={() =>
                      setDraftPage((p) => Math.min(pageCount(filteredDraftSuppliers.length), p + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  <span className="font-medium">{nameOf(deleteTarget)}</span> and its
                  onboarding record will be permanently removed. This cannot be undone.
                  A supplier with purchase orders or quality records against it cannot be
                  deleted — deactivate it instead.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
